import { useQuery } from "@tanstack/react-query";

import { useDataApi } from "@core/apis";

import { PropAIApi } from "../propai.api";
import {
  getFlexStationLiveInsightsOptions,
  getFlexStationsPerSubstationOptions,
  getUrgentNotificationsOptions,
} from "../queries";
import { normalizeAlerts } from "../types/alerts.type";
import { categorizeInsights, normalizeInsights } from "../types/insights.type";

export const useAlerts = (substationId: string, organizationId: string) => {
  const dataApi = useDataApi() as PropAIApi;

  const { data, isLoading } = useQuery({
    ...getUrgentNotificationsOptions(dataApi, {
      status_filter: "all",
      substation_uid: substationId,
      organization_uid: organizationId,
      limit: 100,
      offset: 0,
    }),
  });

  const rawAlerts = data?.notifications || [];
  const alerts = normalizeAlerts(rawAlerts);

  return {
    alerts,
    isLoading,
    isEmpty: !isLoading && alerts.length === 0,
  };
};

export const useInsights = (substationId: string) => {
  const dataApi = useDataApi() as PropAIApi;

  // Fetch the flexstation for substation
  const { data: flexStations, isLoading: isLoadingFlexStations } = useQuery({
    ...getFlexStationsPerSubstationOptions(dataApi, substationId),
  });

  // Get the first flexstation UID
  const flexstationUid = flexStations?.[0]?.uid ?? null;

  // Fetch insights data
  const { data: insightsResponse, isLoading: isLoadingInsights } = useQuery({
    ...getFlexStationLiveInsightsOptions(dataApi, flexstationUid ?? undefined),
    enabled: !!flexstationUid,
  });

  const isLoading = isLoadingFlexStations || isLoadingInsights;

  // Normalize the API response to our component format
  const insights = insightsResponse ? normalizeInsights(insightsResponse) : [];

  // Categorize good and bad insights
  const { good: goodInsights, bad: badInsights } = categorizeInsights(insights);

  return {
    insights,
    goodInsights,
    badInsights,
    isLoading,
    isEmpty: !isLoading && goodInsights.length === 0 && badInsights.length === 0,
  };
};
